import React, { useState, useRef, useEffect } from "react";
import { signOut } from "firebase/auth";
import { auth, firestore } from "../firebase.js";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext.js";
import ThemeToggle from '../ThemeToggle';
import "./Header.css";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Menu, BarChart3, User, LogOut, Package } from "lucide-react";

const Header = ({ openAuthModal }) => {
  const { user, authLoading } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [dropdownImageLoadError, setDropdownImageLoadError] = useState(false);
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const dropdownRef = useRef(null);
  const location = useLocation();
  // After: const location = useLocation();
const isLandingPage = location.pathname === "/";
  const navigate = useNavigate();

  // Fetch profile picture from Firestore
  useEffect(() => {
    const fetchProfilePic = async () => {
      if (!user) {
        setProfilePicUrl('');
        return;
      }

      try {
        const userRef = doc(firestore, 'users', user.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();
          setProfilePicUrl(data.photoURL || user.photoURL || '');
        } else {
          setProfilePicUrl(user.photoURL || '');
        }
      } catch (error) {
        console.error('Error fetching profile pic:', error);
        setProfilePicUrl(user.photoURL || '');
      }
    };

    fetchProfilePic();
  }, [user, user?.photoURL]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const getInitials = (email) => {
    if (!email) return "U";
    const name = email.split("@")[0];
    return name.charAt(0).toUpperCase();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getProfileImageUrl = () => {
    return profilePicUrl || null;
  };

  const getUserPlan = () => {
    return "Free";
  };

  useEffect(() => {
    setImageLoadError(false);
    setDropdownImageLoadError(false);
  }, [profilePicUrl]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (authLoading) {
    return (
     <header className={`header${isLandingPage ? "" : " header--light"}`}>
        <div className="logo-container">
          <img
            src="/logo192.png"
            alt="Prompttar.ai Logo"
            className="header-logo"
          />
          <h1 className="logo-text">Prompttar.ai</h1>
        </div>
        <div className="button-group">
          <div>Loading...</div>
        </div>
      </header>
    );
  }

  const profileImageUrl = getProfileImageUrl();
  const shouldShowImage = profileImageUrl && profileImageUrl.trim() !== "" && !imageLoadError;
  const shouldShowDropdownImage = profileImageUrl && profileImageUrl.trim() !== "" && !dropdownImageLoadError;
  const userPlan = getUserPlan();

  return (
 
    <header className={`header${isLandingPage ? "" : " header--light"}`}>
      <Link to="/" className="logo-container">
        <img
          src="/logo192.png"
          alt="Promptsay.ai Logo"
          className="header-logo"
        />
        <h1 className="logo-text">Prompttar.ai</h1>
      </Link>
      <nav className="header-nav">
        <Link to="/help-resources" className="nav-link">Resources</Link>
        <Link to="/pricing" className="nav-link">Pricing</Link>
        <button
          type="button"
          className="nav-link nav-link--button"
          style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer" }}
          onClick={() => (user ? navigate("/tasks") : openAuthModal("signup"))}
        >
          Try Free
        </button>
      </nav>
      <div className="button-group">
        {user ? (
          <div className="user-section" ref={dropdownRef}>
            <div className="user-info">
              <div className="profile-dropdown">
                <button
                  className="profile-button"
                  onClick={toggleDropdown}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                >
                  <div className="profile-avatar">
                    {shouldShowImage ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="profile-image"
                        onError={() => setImageLoadError(true)}
                        onLoad={() => setImageLoadError(false)}
                      />
                    ) : (
                      <span className="profile-initials">
                        {getInitials(user.email)}
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={`dropdown-arrow ${isDropdownOpen ? "open" : ""}`}
                    size={12}
                  />
                </button>

                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        {shouldShowDropdownImage ? (
                          <img
                            src={profileImageUrl}
                            alt="Profile"
                            className="dropdown-profile-image"
                            onError={() => setDropdownImageLoadError(true)}
                            onLoad={() => setDropdownImageLoadError(false)}
                          />
                        ) : (
                          <span className="dropdown-profile-initials">
                            {getInitials(user.email)}
                          </span>
                        )}
                      </div>
                      <div className="dropdown-user-info">
                        <div className="dropdown-email">{user.email}</div>
                        <div className="dropdown-status">
                          <span>✨</span>{userPlan} Plan
                        </div>
                      </div>
                    </div>

                    <div className="dropdown-divider"></div>

                    <div className="dropdown-items">
                      <Link
                        to="/tasks"
                        className="dropdown-item-header"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <Menu className="dropdown-icon-header" size={16} />
                        Challenges
                      </Link>
                      <Link
                        to="/dashboard"
                        className="dropdown-item-header"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <BarChart3 className="dropdown-icon-header" size={16} />
                        Dashboard
                      </Link>
                      <Link
                        to="/dashboard/savedchallenges"
                        className="dropdown-item-header"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <Package className="dropdown-icon-header" size={16} />
                        Saved Challenges
                      </Link>
                      <Link
                        to="/profile"
                        className="dropdown-item-header"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <User className="dropdown-icon-header" size={16} />
                        Profile
                      </Link>

                      <ThemeToggle />
                    </div>

                    <div className="dropdown-divider"></div>

                    <button
                      className="dropdown-item-header logout-item"
                      onClick={handleLogout}
                    >
                      <LogOut className="dropdown-icon-header" size={16} />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <button className="btn-outline-header" onClick={() => openAuthModal("login")}>
              Log In
            </button>
            <button className="btn-primary-header" onClick={() => openAuthModal("signup")}>
              Sign Up for Free
            </button>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;